/**
 * 全局应用更新 IPC 监听 hook
 * 在 App.tsx 中调用一次，负责监听主进程更新状态推送并同步到 zustand store。
 * 同时处理上报和 toast 等副作用。
 */

import { useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { IPC } from '../../common/ipc/channels';
import { APP_REPORT_EVENTS } from '../../common/enums/appReport';
import type { AppUpdateStatusPayload } from '../../common/types/appUpdate';
import { appReporter } from '../utils/appReporter';
import { useAppUpdateStore } from '../store/appUpdate';

function buildUpdateTrackingKey(status?: AppUpdateStatusPayload | null) {
  return [
    status?.latestVersion || '',
    status?.latestBuildVersion || '',
  ].join(':');
}

export function useAppUpdateListener() {
  const [messageApi, contextHolder] = message.useMessage();

  const updateDownloadTrackingKeyRef = useRef('');
  const updateDownloadResultReportedKeyRef = useRef('');
  const updateModalExposeReportedKeyRef = useRef('');

  const reportUpdateDownloadResult = useCallback((
    status: AppUpdateStatusPayload,
    result: 'success' | 'fail',
    reasonCode = '',
    reasonMessage = '',
  ) => {
    void appReporter.reportUpdateEvent(APP_REPORT_EVENTS.UPDATE_DOWNLOAD_RESULT, {
      latestVersion: status.latestVersion || '',
      latestBuildVersion: status.latestBuildVersion ?? '',
      forceUpdate: Boolean(status.forceUpdate),
      result,
      reasonCode,
      reasonMessage,
    });
  }, []);

  const reportUpdateModalExposed = useCallback((status: AppUpdateStatusPayload) => {
    const exposeKey = buildUpdateTrackingKey(status);
    if (!exposeKey || updateModalExposeReportedKeyRef.current === exposeKey) {
      return;
    }

    updateModalExposeReportedKeyRef.current = exposeKey;
    void appReporter.reportUpdateEvent(APP_REPORT_EVENTS.UPDATE_MODAL_EXPOSED, {
      latestVersion: status.latestVersion || '',
      latestBuildVersion: status.latestBuildVersion ?? '',
      forceUpdate: Boolean(status.forceUpdate),
      status: status.status,
    });
  }, []);

  useEffect(() => {
    if (!window.electronAPI) {
      return undefined;
    }

    const store = useAppUpdateStore;

    // 窗口挂载时查询主进程缓存的更新状态（覆盖 macOS 关窗后再打开的场景）
    window.electronAPI.invoke(IPC.GET_APP_UPDATE_PENDING_STATUS)
      .then((payload) => {
        const nextStatus = payload as AppUpdateStatusPayload | null;
        if (!nextStatus) return;
        // 用 handleUpdateStatusEvent 统一处理，确保各状态（available/downloading/downloaded）都能恢复
        store.getState().handleUpdateStatusEvent(nextStatus);
      })
      .catch((error) => {
        console.error('Failed to load pending update status.', error);
      });

    // 监听主进程更新状态推送
    const cleanup = window.electronAPI.on(IPC.UPDATE_STATUS, (payload) => {
      const nextStatus = payload as AppUpdateStatusPayload;
      const effects = store.getState().handleUpdateStatusEvent(nextStatus);

      // 上报弹窗曝光
      if (effects.shouldReportExpose) {
        reportUpdateModalExposed(nextStatus);
      }

      // 下载进度追踪（用于上报下载结果）
      if (nextStatus.status === 'downloading') {
        if (!updateDownloadTrackingKeyRef.current) {
          updateDownloadTrackingKeyRef.current = buildUpdateTrackingKey(nextStatus);
        }
      }

      if (nextStatus.status === 'downloaded') {
        const trackingKey = buildUpdateTrackingKey(nextStatus);
        if (
          trackingKey
          && updateDownloadTrackingKeyRef.current === trackingKey
          && updateDownloadResultReportedKeyRef.current !== `${trackingKey}:success`
        ) {
          reportUpdateDownloadResult(nextStatus, 'success');
          updateDownloadResultReportedKeyRef.current = `${trackingKey}:success`;
          updateDownloadTrackingKeyRef.current = '';
        }
      }

      // "当前已是最新版本" toast
      if (effects.shouldShowNotAvailableToast) {
        messageApi.success('当前已是最新版本');
      }

      // 错误处理
      if (nextStatus.status === 'error') {
        const trackingKey = buildUpdateTrackingKey(nextStatus);
        if (
          updateDownloadTrackingKeyRef.current
          && updateDownloadResultReportedKeyRef.current !== `${updateDownloadTrackingKeyRef.current}:fail`
        ) {
          reportUpdateDownloadResult(
            nextStatus,
            'fail',
            'download_error',
            nextStatus.errorMessage || '更新包下载失败',
          );
          updateDownloadResultReportedKeyRef.current = `${updateDownloadTrackingKeyRef.current}:fail`;
          updateDownloadTrackingKeyRef.current = '';
        } else if (
          trackingKey
          && updateDownloadResultReportedKeyRef.current !== `${trackingKey}:fail`
          && nextStatus.errorMessage
        ) {
          reportUpdateDownloadResult(nextStatus, 'fail', 'download_error', nextStatus.errorMessage);
          updateDownloadResultReportedKeyRef.current = `${trackingKey}:fail`;
        }
      }

      if (effects.errorToastMessage) {
        messageApi.error(effects.errorToastMessage);
      }
    });

    return () => {
      cleanup();
    };
  }, [messageApi, reportUpdateDownloadResult, reportUpdateModalExposed]);

  return { contextHolder };
}
